<template>
  <!-- Žádné dostupné typy → malý sheet s informací -->
  <BottomSheetModal
    v-if="!eventToEdit && eventTypesFetched && eventTypes.length === 0"
    v-model="isOpen"
    autoHeight
    hideConfirm
    @close="handleClose"
  >
    <template #title>{{ $t('eventForm.newEvent') }}</template>
    <div style="padding: 24px 16px 16px;">
      <div class="d-flex align-start" style="gap: 12px;">
        <v-icon color="info" size="28">mdi-information-outline</v-icon>
        <span class="text-body-1">
          {{ $t('eventForm.noTeamWarning') }}
        </span>
      </div>
    </div>
  </BottomSheetModal>

  <!-- Normální formulář -->
  <BottomSheetModal
    v-else
    v-model="isOpen"
    :confirmDisabled="!canSubmit"
    :hideHeader="currentView !== 'main'"
    @confirm="handleSave"
    @close="handleClose"
  >
    <template #title>{{ eventToEdit ? $t('eventForm.editEvent') : $t('eventForm.newEvent') }}</template>

    <div class="views-container">
      <!-- ═══ MAIN VIEW ═══ -->
      <div class="main-view" :class="{ 'main-view--hidden': currentView !== 'main' }">

        <!-- Event type -->
        <IosSection form :title="$t('eventForm.eventType')" required>
          <IosRow
            :label="$t('eventForm.eventType')"
            :value="selectedType?.name || $t('eventForm.selectType')"
            :clickable="!isEditing"
            :first="true"
            :last="!showIconPicker"
            @click="!isEditing && (currentView = 'typeSelect')"
          >
            <template #icon>
              <v-icon v-if="selectedType" :color="calendarColors(selectedType.color)" size="20">mdi-circle</v-icon>
              <v-icon v-else size="20" color="grey">mdi-circle-outline</v-icon>
            </template>
          </IosRow>
          <IosRow
            v-if="showIconPicker"
            :label="$t('eventForm.icon')"
            :value="selectedIconLabel"
            clickable
            last
            @click="currentView = 'iconSelect'"
          >
            <template #icon>
              <v-icon size="20" :color="calendarColors(selectedType?.color || 'grey')">{{ selectedIconMdi }}</v-icon>
            </template>
          </IosRow>
        </IosSection>

        <!-- ═══ PLAYOFF SETTINGS ═══ -->
        <IosSection v-if="showPlayoffSection" form :title="$t('eventForm.playoffTitle')">
          <IosSwitch
            :modelValue="!!form.playoffMeta"
            @update:modelValue="togglePlayoffEvent"
            :label="$t('eventForm.playoffEvent')"
            first
            :last="!form.playoffMeta"
          />
          <template v-if="form.playoffMeta">
            <!-- Phase picker (groups / playoff / consolation) -->
            <IosRow
              :label="$t('eventForm.playoffPhase')"
              :value="selectedPhaseLabel"
              clickable
              @click="currentView = 'playoffPhaseSelect'"
            >
              <template #icon>
                <v-icon size="20">mdi-layers-outline</v-icon>
              </template>
            </IosRow>
            <!-- Branch picker (only for playoff phase with branches) -->
            <IosRow
              v-if="isPlayoffPhase && seasonPlayoffState?.branches?.length"
              :label="$t('eventForm.playoffBranch')"
              :value="selectedBranchLabel"
              clickable
              @click="currentView = 'playoffBranchSelect'"
            >
              <template #icon>
                <v-icon size="20">mdi-source-branch</v-icon>
              </template>
            </IosRow>
            <!-- Round type picker (only for playoff phase) -->
            <IosRow
              v-if="isPlayoffPhase && roundTypeOptions.length"
              :label="$t('eventForm.playoffRound')"
              :value="selectedRoundTypeLabel"
              clickable
              @click="currentView = 'playoffRoundSelect'"
            >
              <template #icon>
                <v-icon size="20">mdi-tournament</v-icon>
              </template>
            </IosRow>
            <!-- Group picker (for groups / consolation) -->
            <IosRow
              v-if="isGroupsPhase || isConsolationPhase"
              :label="$t('eventForm.playoffGroup')"
              :value="selectedGroupLabel"
              clickable
              last
              @click="currentView = 'playoffGroupSelect'"
            >
              <template #icon>
                <v-icon size="20">mdi-account-group</v-icon>
              </template>
            </IosRow>
            <div v-else class="inline-datetime-row last" style="height: 0; padding: 0; margin: 0; border: none;" />
          </template>
        </IosSection>

        <!-- ═══ TEAM FOR BLUE-WHITE ═══ -->
        <IosSection v-if="form.type === 'blue-white'" form :title="$t('eventForm.team')">
          <IosRow
            :label="selectedBlueWhiteTeam?.name || $t('eventForm.selectTeam')"
            :clickable="!isEditing"
            first
            last
            @click="!isEditing && (currentView = 'blueWhiteTeamSelect')"
          >
            <template #icon>
              <v-avatar v-if="selectedBlueWhiteTeam" size="28">
                <v-img :src="getTeamImage(selectedBlueWhiteTeam.imageUrl)" alt="team" />
              </v-avatar>
              <v-icon v-else size="20" color="grey">mdi-account-group-outline</v-icon>
            </template>
          </IosRow>
        </IosSection>

        <!-- ═══ MATCH: HOME TEAM + AWAY TEAM side by side ═══ -->
        <template v-if="isMatchType">
          <!-- ═══ DOMÁCÍ TÝM ═══ -->
          <div class="section-title">{{ $t('eventForm.homeTeam') }}</div>
          <div class="team-jersey-row">
            <IosSection form>
              <IosRow
                :label="selectedHomeTeam?.name || $t('eventForm.selectTeam')"
                clickable
                :first="true"
                :last="!form.playoffMeta || !!selectedHomeTeam"
                @click="handleTeamClick('home')"
              >
                <template #icon>
                  <v-avatar v-if="selectedHomeTeam" size="28">
                    <v-img :src="getTeamImage(selectedHomeTeam.imageUrl)" alt="team" />
                  </v-avatar>
                  <v-icon v-else size="20" color="grey">mdi-account-group-outline</v-icon>
                </template>
              </IosRow>
              <IosRow
                v-if="form.playoffMeta && !selectedHomeTeam"
                :label="$t('eventForm.slotLabelPlaceholder')"
                :value="form.playoffMeta.groupSlotHomeLabel || ''"
                clickable
                last
                @click="pendingSlotSide = 'home'; currentView = 'homeSlotLabelSelect'"
              >
                <template #icon>
                  <v-icon size="20" color="grey">mdi-tag-outline</v-icon>
                </template>
              </IosRow>
            </IosSection>

            <IosSection v-if="homeTeamColors.length > 0" form>
              <IosRow
                :label="$t('eventForm.jerseyLabel')"
                clickable
                first
                last
                @click="openJerseyPicker('home')"
              >
                <template #value>
                  <div v-if="form.meta.homeColor" class="jersey-preview">
                    <img :src="getDressImage(form.meta.homeColor)" alt="" />
                  </div>
                  <span v-else class="text-disabled">{{ $t('eventForm.jerseySelect') }}</span>
                </template>
              </IosRow>
            </IosSection>
          </div>

          <!-- ═══ HOSTUJÍCÍ TÝM ═══ -->
          <div class="section-title">{{ $t('eventForm.awayTeam') }}</div>
          <div class="team-jersey-row">
            <IosSection form>
              <IosRow
                :label="selectedAwayTeam?.name || $t('eventForm.selectTeam')"
                clickable
                :first="true"
                :last="!form.playoffMeta || !!selectedAwayTeam"
                @click="handleTeamClick('away')"
              >
                <template #icon>
                  <v-avatar v-if="selectedAwayTeam" size="28">
                    <v-img :src="getTeamImage(selectedAwayTeam.imageUrl)" alt="team" />
                  </v-avatar>
                  <v-icon v-else size="20" color="grey">mdi-account-group-outline</v-icon>
                </template>
              </IosRow>
              <IosRow
                v-if="form.playoffMeta && !selectedAwayTeam"
                :label="$t('eventForm.slotLabelPlaceholder')"
                :value="form.playoffMeta.groupSlotAwayLabel || ''"
                clickable
                last
                @click="pendingSlotSide = 'away'; currentView = 'awaySlotLabelSelect'"
              >
                <template #icon>
                  <v-icon size="20" color="grey">mdi-tag-outline</v-icon>
                </template>
              </IosRow>
            </IosSection>

            <IosSection v-if="awayTeamColors.length > 0" form>
              <IosRow
                :label="$t('eventForm.jerseyLabel')"
                clickable
                first
                last
                @click="openJerseyPicker('away')"
              >
                <template #value>
                  <div v-if="form.meta.awayColor" class="jersey-preview">
                    <img :src="getDressImage(form.meta.awayColor)" alt="" />
                  </div>
                  <span v-else class="text-disabled">{{ $t('eventForm.jerseySelect') }}</span>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </template>

        <!-- Event name -->
        <div class="custom-section">
          <div class="section-title">{{ $t('eventForm.titleLabel') }} <span class="optional-hint">{{ $t('eventForm.optional') }}</span></div>
          <IosSection form>
            <IosInput
              v-model="form.title"
              :placeholder="$t('eventNamePlaceholder')"
              :maxlength="60"
              first
              last
            />
          </IosSection>
        </div>

        <!-- Event description (collapsible) -->
        <IosSection form :title="$t('eventForm.descriptionTitle')">
          <div class="inline-datetime-row first" :class="{ last: !descriptionOpen }" @click="descriptionOpen = !descriptionOpen" style="cursor: pointer;">
            <label class="inline-label">{{ $t('eventForm.addDescription') }}</label>
            <v-icon size="18" class="desc-toggle-row__chevron" :class="{ open: descriptionOpen }">mdi-chevron-down</v-icon>
          </div>
          <Transition name="section-collapse">
          <div v-show="descriptionOpen" class="description-editor-wrap">
            <div class="description-toolbar">
              <button type="button" class="desc-tb-btn" :class="{ active: descHeading === 'h3' }" @click="toggleHeading('h3')" :title="$t('eventForm.descHeading')">
                <span class="desc-tb-txt">H1</span>
              </button>
              <button type="button" class="desc-tb-btn" :class="{ active: descHeading === 'h4' }" @click="toggleHeading('h4')" :title="$t('eventForm.descSubheading')">
                <span class="desc-tb-txt desc-tb-txt--sm">H2</span>
              </button>
              <button type="button" class="desc-tb-btn" :class="{ active: descBold }" @click="toggleDescBold" title="Bold">
                <v-icon size="18">mdi-format-bold</v-icon>
              </button>
              <span class="desc-tb-sep" />
              <button
                v-for="c in descColorOptions"
                :key="c.value"
                type="button"
                class="desc-tb-color"
                :class="{ active: descActiveColor === c.value }"
                :style="{ '--dot': c.hex }"
                @click="applyDescColor(c.value)"
                :title="c.label"
              />
            </div>
            <div class="desc-editor-scroll-wrap" :class="{ 'shadow-top': descScrollTop, 'shadow-bot': descScrollBot }">
              <div
                ref="descEditorRef"
                class="description-editor"
                contenteditable="true"
                :data-placeholder="$t('eventForm.descriptionPlaceholder')"
                @input="onDescInput"
                @keydown="onDescKeydown"
                @keyup="onDescInput(); updateToolbarState()"
                @mouseup="updateToolbarState"
                @scroll="updateDescScroll"
                @paste="nextTick(onDescInput)"
              />
            </div>
          </div>
          </Transition>
        </IosSection>

        <!-- Date/Time — inline native inputs -->
        <IosSection form :title="$t('eventForm.startTitle')" required>
          <div class="inline-datetime-row first">
            <label class="inline-label">{{ $t('eventForm.date') }}</label>
            <input type="date" class="inline-input" v-model="form.dateFrom" />
          </div>
          <div class="inline-datetime-row last">
            <label class="inline-label">{{ $t('eventForm.time') }}</label>
            <input type="time" class="inline-input" v-model="form.timeFrom" />
          </div>
        </IosSection>

        <IosSection form :title="$t('eventForm.endTitle')">
          <div class="inline-datetime-row first">
            <label class="inline-label">{{ $t('eventForm.date') }}</label>
            <input type="date" class="inline-input" v-model="form.dateTo" />
          </div>
          <div class="inline-datetime-row last">
            <label class="inline-label">{{ $t('eventForm.time') }}</label>
            <input type="time" class="inline-input" v-model="form.timeTo" />
          </div>
        </IosSection>

        <!-- Location -->
        <IosSection form :title="$t('eventForm.venueTitle')">
          <IosInput
            v-model="form.place"
            :placeholder="$t('eventForm.venuePlaceholder')"
            first
            last
          />
        </IosSection>

        <!-- Price -->
        <IosSection form :title="$t('eventForm.paymentTitle')">
          <div class="inline-datetime-row first">
            <label class="inline-label">{{ $t('eventForm.playerPayment') }}</label>
            <div class="price-input-wrapper">
              <input type="number" class="inline-input" v-model.number="form.ammountCzk" min="0" max="99999" />
              <span class="price-suffix">{{ $t('registration.currency') }}</span>
            </div>
          </div>
          <div class="inline-datetime-row last">
            <label class="inline-label">{{ $t('eventForm.goaliePayment') }}</label>
            <div class="price-input-wrapper">
              <input type="number" class="inline-input" v-model.number="form.ammountGoalieCzk" min="0" max="99999" />
              <span class="price-suffix">{{ $t('registration.currency') }}</span>
            </div>
          </div>
        </IosSection>

        <!-- Pin -->
        <IosSection form>
          <IosSwitch
            v-model="form.pin"
            :label="$t('eventForm.pinEvent')"
            first
            last
          />
        </IosSection>

        <!-- Settings — proper switches -->
        <IosSection form :title="$t('eventForm.settings')">
          <IosSwitch
            v-model="form.public"
            :label="$t('eventForm.publicEvent')"
            first
          />
          <IosSwitch
            v-model="form.allowExternals"
            :label="$t('eventForm.allowForeignPlayers')"
          />
          <IosSwitch
            v-model="form.allInDefault"
            :label="$t('eventForm.reportAttendance')"
            last
          />
        </IosSection>

        <!-- Player/goalie counts (training / blue-white) -->
        <IosSection v-if="form.type === 'blue-white' || form.type === 'training'" form :title="$t('eventForm.countsTitle')">
          <div class="inline-datetime-row first">
            <label class="inline-label">{{ $t('eventForm.playersLabel') }}</label>
            <input type="number" class="inline-input" v-model.number="form.meta.players" min="1" max="50" />
          </div>
          <div class="inline-datetime-row last">
            <label class="inline-label">{{ $t('eventForm.goaliesLabel') }}</label>
            <input type="number" class="inline-input" v-model.number="form.meta.goalies" min="0" max="10" />
          </div>
        </IosSection>

        <!-- Queue player counts (match types) -->
        <IosSection v-if="isMatchType && form.queue && !form.allInDefault" form :title="$t('eventForm.playerCountsTitle')">
          <div class="inline-datetime-row first">
            <label class="inline-label">{{ $t('eventForm.homePlayersCount') }}</label>
            <input type="number" class="inline-input" v-model.number="form.meta.homePlayers" min="1" max="30" />
          </div>
          <div class="inline-datetime-row">
            <label class="inline-label">{{ $t('eventForm.homeGoaliesCount') }}</label>
            <input type="number" class="inline-input" v-model.number="form.meta.homeGoalies" min="0" max="5" />
          </div>
          <div class="inline-datetime-row">
            <label class="inline-label">{{ $t('eventForm.awayPlayersCount') }}</label>
            <input type="number" class="inline-input" v-model.number="form.meta.awayPlayers" min="1" max="30" />
          </div>
          <div class="inline-datetime-row last">
            <label class="inline-label">{{ $t('eventForm.awayGoaliesCount') }}</label>
            <input type="number" class="inline-input" v-model.number="form.meta.awayGoalies" min="0" max="5" />
          </div>
        </IosSection>

        <!-- Weekly copy -->
        <IosSection form :title="$t('eventForm.copyTitle')">
          <div class="inline-datetime-row first last">
            <label class="inline-label">{{ $t('eventForm.copyWeekly') }}</label>
            <input type="number" class="inline-input" v-model.number="form.weeklyCopy" min="0" max="10" />
          </div>
        </IosSection>

        <!-- Game settings -->
        <IosSection v-if="form.type !== 'training' && form.type !== 'camp'" form :title="$t('eventForm.timerTitle')">
          <IosRow
            :label="$t('eventForm.gameSettingsLabel')"
            :value="gameSettingsSummary"
            clickable
            first
            last
            @click="currentView = 'gameSettings'"
          >
            <template #icon>
              <v-icon size="20">mdi-timer-outline</v-icon>
            </template>
            <template #append>
              <v-icon size="20" color="grey">mdi-chevron-right</v-icon>
            </template>
          </IosRow>
        </IosSection>

        <!-- Match score toggle -->
        <IosSection v-if="isMatchType && selectedHomeTeam && selectedAwayTeam" form :title="$t('eventForm.matchScoreTitle')">
          <IosSwitch
            :modelValue="scoreEnabled"
            @update:modelValue="onScoreToggle"
            :label="$t('eventForm.enterScore')"
            first
            :last="!scoreEnabled"
          />
          <template v-if="scoreEnabled">
            <div class="inline-datetime-row">
              <label class="inline-label score-team-label">
                <v-avatar size="22" class="score-team-avatar">
                  <v-img :src="getTeamImage(selectedHomeTeam.imageUrl)" alt="home" />
                </v-avatar>
                <span class="score-team-name">{{ selectedHomeTeam.name }}</span>
              </label>
              <input type="number" class="inline-input" v-model.number="matchScoreHome" min="0" max="99" @input="markScoreDirty" />
            </div>
            <div class="inline-datetime-row" :class="{ last: !showResolutionPicker }">
              <label class="inline-label score-team-label">
                <v-avatar size="22" class="score-team-avatar">
                  <v-img :src="getTeamImage(selectedAwayTeam.imageUrl)" alt="away" />
                </v-avatar>
                <span class="score-team-name">{{ selectedAwayTeam.name }}</span>
              </label>
              <input type="number" class="inline-input" v-model.number="matchScoreAway" min="0" max="99" @input="markScoreDirty" />
            </div>
            <div v-if="showResolutionPicker" class="inline-datetime-row last">
              <label class="inline-label">{{ $t('eventForm.resolution') }}</label>
              <div class="resolution-picker">
                <button
                  v-for="opt in resolutionOptions"
                  :key="opt.value"
                  class="resolution-btn"
                  :class="{ active: matchResolution === opt.value }"
                  @click="selectMatchResolution(opt.value)"
                >{{ opt.label }}</button>
              </div>
            </div>
          </template>
        </IosSection>

        <!-- Score confirm dialog -->
        <CenteredModal v-model="scoreConfirmDialog" :showCloseButton="false" maxWidth="360px" :zIndex="10001">
          <div class="score-confirm-dialog">
            <v-icon size="48" color="info" class="score-confirm-icon">mdi-scoreboard-outline</v-icon>
            <h3 class="score-confirm-title">{{ $t('eventForm.scoreConfirmTitle') }}</h3>
            <p class="score-confirm-text">{{ scoreConfirmMessage }}</p>
            <div class="score-confirm-actions">
              <button class="score-confirm-btn score-confirm-btn--cancel" @click="scoreConfirmDialog = false">{{ $t('eventForm.scoreConfirmCancel') }}</button>
              <button class="score-confirm-btn score-confirm-btn--confirm" @click="confirmScoreToggle">{{ $t('eventForm.scoreConfirmOk') }}</button>
            </div>
          </div>
        </CenteredModal>

        <!-- Team change warning dialog -->
        <CenteredModal v-model="teamChangeWarningDialog" :showCloseButton="false" maxWidth="360px" :zIndex="10001">
          <div class="score-confirm-dialog">
            <v-icon size="48" color="warning" class="score-confirm-icon">mdi-alert-outline</v-icon>
            <h3 class="score-confirm-title">{{ $t('eventForm.teamChangeWarningTitle') }}</h3>
            <p class="score-confirm-text">{{ $t('eventForm.teamChangeWarningText') }}</p>
            <div class="score-confirm-actions">
              <button class="score-confirm-btn score-confirm-btn--cancel" @click="teamChangeWarningDialog = false">{{ $t('eventForm.scoreConfirmCancel') }}</button>
              <button class="score-confirm-btn score-confirm-btn--confirm" @click="confirmTeamChange">{{ $t('eventForm.scoreConfirmOk') }}</button>
            </div>
          </div>
        </CenteredModal>

      </div>

      <!-- ═══ TYPE SELECT SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'typeSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.selectEventType') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <div v-if="eventTypesLoading" class="ios-loading">
              <v-progress-circular indeterminate size="24" />
            </div>
            <IosSection v-else form>
              <IosRow
                v-for="(et, index) in eventTypes"
                :key="et.value + '-' + (et._league || '') + '-' + (et._season || '')"
                :label="et.name"
                :first="index === 0"
                :last="index === eventTypes.length - 1"
                clickable
                @click="selectEventType(et); currentView = 'main'"
              >
                <template #icon>
                  <v-icon :color="calendarColors(et.color)" size="20">mdi-circle</v-icon>
                </template>
                <template #value>
                  <v-icon v-if="isSelectedType(et)" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- ═══ HOME TEAM SELECT SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'homeTeamSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.selectHomeTeam') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection v-if="form.meta.homeTeam" form>
              <IosRow
                :label="$t('eventForm.removeTeam')"
                first
                last
                clickable
                @click="removeTeam('home')"
              >
                <template #icon>
                  <v-icon size="20" color="error">mdi-close-circle-outline</v-icon>
                </template>
              </IosRow>
            </IosSection>
            <IosSection form>
              <IosRow
                v-for="(team, index) in availableHomeTeams"
                :key="team._id"
                :label="team.name"
                :first="index === 0"
                :last="index === availableHomeTeams.length - 1"
                clickable
                @click="selectHomeTeam(team)"
              >
                <template #icon>
                  <v-avatar size="28">
                    <v-img :src="getTeamImage(team.imageUrl)" alt="team" />
                  </v-avatar>
                </template>
                <template #value>
                  <v-icon v-if="form.meta.homeTeam === team._id" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- ═══ AWAY TEAM SELECT SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'awayTeamSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.selectAwayTeam') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection v-if="form.meta.awayTeam" form>
              <IosRow
                :label="$t('eventForm.removeTeam')"
                first
                last
                clickable
                @click="removeTeam('away')"
              >
                <template #icon>
                  <v-icon size="20" color="error">mdi-close-circle-outline</v-icon>
                </template>
              </IosRow>
            </IosSection>
            <IosSection form>
              <IosRow
                v-for="(team, index) in availableAwayTeams"
                :key="team._id"
                :label="team.name"
                :first="index === 0"
                :last="index === availableAwayTeams.length - 1"
                clickable
                @click="selectAwayTeam(team)"
              >
                <template #icon>
                  <v-avatar size="28">
                    <v-img :src="getTeamImage(team.imageUrl)" alt="team" />
                  </v-avatar>
                </template>
                <template #value>
                  <v-icon v-if="form.meta.awayTeam === team._id" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- ═══ BLUE-WHITE TEAM SELECT SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'blueWhiteTeamSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.selectTeam') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow
                v-for="(team, index) in availableTeamsForForm"
                :key="team._id"
                :label="team.name"
                :first="index === 0"
                :last="index === availableTeamsForForm.length - 1"
                clickable
                @click="selectBlueWhiteTeam(team)"
              >
                <template #icon>
                  <v-avatar size="28">
                    <v-img :src="getTeamImage(team.imageUrl)" alt="team" />
                  </v-avatar>
                </template>
                <template #value>
                  <v-icon v-if="form._team === team._id" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- ═══ ICON PICKER SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'iconSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.selectIcon') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <div class="icons-grid">
              <div
                v-for="opt in eventIconOptions"
                :key="opt.value"
                class="icon-item"
                :class="{ 'icon-item--selected': form.icon === opt.value }"
                @click="selectIcon(opt.value)"
              >
                <v-icon size="36" :color="form.icon === opt.value ? calendarColors(selectedType?.color || 'grey') : 'rgba(var(--v-theme-sheetText), 0.6)'">{{ opt.icon }}</v-icon>
                <div class="icon-label">{{ opt.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- ═══ GAME SETTINGS SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'gameSettings'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.gameSettingsSubview') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <!-- Pravidla bodování -->
            <IosSection form :title="$t('eventForm.scoringRulesTitle')">
              <IosSwitch
                v-model="form.splitPointsByParts"
                :label="$t('eventForm.splitPoints')"
                :subtitle="$t('eventForm.splitPointsDesc')"
                first
              />
              <div class="inline-datetime-row">
                <label class="inline-label">{{ $t('eventForm.winPoints') }}</label>
                <input type="number" class="inline-input" v-model.number="form.pointsWin" min="0" />
              </div>
              <div class="inline-datetime-row">
                <label class="inline-label">{{ $t('eventForm.overtimeWinPoints') }}</label>
                <input type="number" class="inline-input" v-model.number="form.pointsOvertimeWin" min="0" />
              </div>
              <div class="inline-datetime-row last">
                <label class="inline-label">{{ $t('eventForm.overtimeLossPoints') }}</label>
                <input type="number" class="inline-input" v-model.number="form.pointsOvertimeLoss" min="0" />
              </div>
            </IosSection>
            <!-- Nastavení hry -->
            <IosSection form :title="$t('eventForm.gameSettingsSection')">
              <div class="inline-datetime-row first">
                <label class="inline-label">{{ $t('eventForm.periodsCount') }}</label>
                <input type="number" class="inline-input" v-model.number="form.partsCount" min="1" max="5" />
              </div>
              <div class="inline-datetime-row">
                <label class="inline-label">{{ $t('eventForm.periodLength') }}</label>
                <input type="number" class="inline-input" v-model.number="form.partTime" min="1" max="60" />
              </div>
              <div class="inline-datetime-row">
                <label class="inline-label">{{ $t('eventForm.playersCount') }}</label>
                <button class="inline-button" @click="togglePlayersCount">{{ form.playersCount }}</button>
              </div>
              <div class="inline-datetime-row">
                <label class="inline-label">{{ $t('eventForm.overtimeLength') }}</label>
                <input type="number" class="inline-input" v-model.number="form.overtimeDuration" min="1" max="20" />
              </div>
              <div class="inline-datetime-row">
                <label class="inline-label">{{ $t('eventForm.minorPenalty') }}</label>
                <input type="number" class="inline-input" v-model.number="form.penaltySmall" min="0" max="10" step="0.5" />
              </div>
              <div class="inline-datetime-row">
                <label class="inline-label">{{ $t('eventForm.majorPenalty') }}</label>
                <input type="number" class="inline-input" v-model.number="form.penaltyBig" min="0" max="20" />
              </div>
              <div class="inline-datetime-row last">
                <label class="inline-label">{{ $t('eventForm.misconductPenalty') }}</label>
                <input type="number" class="inline-input" v-model.number="form.penaltyPersonal" min="0" max="30" />
              </div>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- ═══ JERSEY PICKER SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'homeJersey' || currentView === 'awayJersey'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">
              {{ currentView === 'homeJersey' ? $t('eventForm.homeJersey') : $t('eventForm.awayJersey') }}
            </div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <div class="jerseys-grid">
              <div
                v-for="color in jerseyPickerColors"
                :key="color"
                class="jersey-item"
                :class="{ 'jersey-item--selected': jerseyPickerValue === color }"
                @click="selectJersey(color)"
              >
                <div class="jersey-dress-wrapper">
                  <img :src="getDressImage(color)" :alt="color" class="jersey-dress-img" />
                  <img
                    v-if="jerseyPickerTeamImage"
                    :src="getTeamImage(jerseyPickerTeamImage)"
                    alt=""
                    class="jersey-team-logo"
                  />
                </div>
                <div class="jersey-label">{{ color.replace('-', ' / ').toUpperCase() }}</div>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- ═══ PLAYOFF PHASE SELECT SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'playoffPhaseSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.playoffSelectPhase') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow
                v-for="(opt, index) in phaseOptions"
                :key="opt.value"
                :label="opt.label"
                :first="index === 0"
                :last="index === phaseOptions.length - 1"
                clickable
                @click="selectPlayoffPhase(opt.value)"
              >
                <template #icon>
                  <v-icon size="20">{{ opt.icon }}</v-icon>
                </template>
                <template #value>
                  <v-icon v-if="form.phase === opt.value" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- ═══ PLAYOFF ROUND SELECT SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'playoffRoundSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.playoffSelectRound') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow
                v-for="(opt, index) in roundTypeOptions"
                :key="opt.value"
                :label="opt.label"
                :first="index === 0"
                :last="index === roundTypeOptions.length - 1"
                clickable
                @click="selectPlayoffRound(opt.value)"
              >
                <template #icon>
                  <v-icon size="20">mdi-trophy-outline</v-icon>
                </template>
                <template #value>
                  <v-icon v-if="form.playoffMeta?.roundType === opt.value" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- ═══ PLAYOFF BRANCH SELECT SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'playoffBranchSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.playoffSelectBranch') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow
                v-for="(branch, index) in (seasonPlayoffState?.branches || [])"
                :key="branch.id"
                :label="branch.name"
                :first="index === 0"
                :last="index === (seasonPlayoffState?.branches?.length ?? 1) - 1"
                clickable
                @click="selectPlayoffBranch(branch.id)"
              >
                <template #icon>
                  <v-icon size="20">mdi-source-branch</v-icon>
                </template>
                <template #value>
                  <v-icon v-if="form.playoffMeta?.branchId === branch.id" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- ═══ PLAYOFF GROUP SELECT SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'playoffGroupSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.playoffSelectGroup') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow
                v-for="(grp, index) in groupOptions"
                :key="grp.value"
                :label="grp.label"
                :first="index === 0"
                :last="index === groupOptions.length - 1"
                clickable
                @click="selectGroupIndex(grp.value)"
              >
                <template #icon>
                  <v-icon size="20">mdi-account-group</v-icon>
                </template>
                <template #value>
                  <v-icon v-if="form.playoffMeta?.groupIndex === grp.value" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- ═══ SLOT LABEL SELECT SUBVIEW ═══ -->
      <Transition name="slide">
        <div v-if="currentView === 'homeSlotLabelSelect' || currentView === 'awaySlotLabelSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('eventForm.slotLabelPlaceholder') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow
                v-for="(opt, index) in slotLabelOptions"
                :key="opt.value"
                :label="opt.label"
                :first="index === 0"
                :last="index === slotLabelOptions.length - 1"
                clickable
                @click="selectSlotLabel(opt.value)"
              >
                <template #icon>
                  <v-icon size="20">mdi-tag-outline</v-icon>
                </template>
                <template #value>
                  <v-icon v-if="(pendingSlotSide === 'home' ? form.playoffMeta?.groupSlotHomeLabel : form.playoffMeta?.groupSlotAwayLabel) === opt.value" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

    </div>
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import BottomSheetModal from '@/components/BottomSheetModal.vue'
import CenteredModal from '@/components/CenteredModal.vue'
import { IosSection, IosRow, IosInput, IosSwitch } from '@/components/ios'
import axiosInstance from '@/services/axiosInstance'
import { calendarColors } from '@/utils/colorUtils'
import { getTeamImage, getDressImage } from '@/utils/imageUtils'
import { useUserStore } from '@/stores/user/userStore'
import { defaultDressColor } from '@/utils/teamUtils'

const userStore = useUserStore()
const { t } = useI18n({ useScope: 'global' })

type ViewType = 'main' | 'typeSelect' | 'homeTeamSelect' | 'awayTeamSelect' | 'blueWhiteTeamSelect' | 'homeJersey' | 'awayJersey' | 'iconSelect' | 'gameSettings' | 'playoffPhaseSelect' | 'playoffRoundSelect' | 'playoffBranchSelect' | 'playoffGroupSelect' | 'homeSlotLabelSelect' | 'awaySlotLabelSelect'

const props = defineProps<{
  modelValue: boolean
  eventToEdit?: any
  initialDate?: Date | null
  presetLeagueId?: string | null
  presetSeasonId?: string | null
  presetTeamId?: string | null
  presetHomeTeamId?: string | null
  presetAwayTeamId?: string | null
  presetEventType?: string | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void
  (e: 'saved', data: any): void
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const isEditing = computed(() => !!props.eventToEdit)
const isTeamLocked = false

const currentView = ref<ViewType>('main')

// ── Change tracking ──
const initialFormSnapshot = ref<string>('')

const takeSnapshot = () => {
  const { weeklyCopy, ...rest } = form.value
  initialFormSnapshot.value = JSON.stringify(rest)
}

const hasChanges = computed(() => {
  if (!isEditing.value) return true
  if (!initialFormSnapshot.value) return false
  const { weeklyCopy, ...rest } = form.value
  return JSON.stringify(rest) !== initialFormSnapshot.value
})

// ── Form ──
const form = ref({
  title: '',
  description: '',
  type: '',
  dateFrom: '',
  timeFrom: '',
  dateTo: '',
  timeTo: '',
  place: 'Haly Lužánky',
  meta: { players: 20, goalies: 2 } as any,
  attendants: {} as any,
  pin: false,
  weeklyCopy: 0,
  _league: null as any,
  _season: null as any,
  content: '',
  allowExternals: false,
  public: false,
  group: null as number | null,
  _team: null as string | null,
  queue: false,
  allInDefault: false,
  partsCount: 3,
  partTime: 15,
  overtimeDuration: 10,
  penaltySmall: 1.5,
  penaltyBig: 3,
  penaltyPersonal: 7,
  playersCount: '5+1',
  splitPointsByParts: false,
  pointsWin: 3,
  pointsOvertimeWin: 2,
  pointsOvertimeLoss: 1,
  timekeepers: [] as string[],
  icon: 'default',
  ammountCzk: 350,
  ammountGoalieCzk: 0,
  phase: null as string | null,
  playoffMeta: null as {
    roundType: string;
    branchId: number | null;
    seriesIndex: number | null;
    gameNumber: number | null;
    label: string;
    groupIndex: number | null;
    round: string | null;
    isDraft: boolean;
    groupSlotHome: number | null;
    groupSlotAway: number | null;
    groupSlotHomeLabel: string | null;
    groupSlotAwayLabel: string | null;
  } | null,
})

// ── Season playoff config (for manual playoff events) ──
const seasonPlayoffState = ref<{
  playoffActive: boolean;
  playoffGroupsActive: boolean;
  branches: Array<{ id: number; name: string; teamCount: number; rounds: Array<{ name: string; series: number }> }>;
  groupCount: number;
  totalTeams: number;
  teamsPerGroup: number;
  regularSeasonGroupCount: number;
  hasPlayoffConfig: boolean;
  hasGroups: boolean;
  consolationGroupEnabled: boolean;
} | null>(null)

const showPlayoffSection = computed(() => {
  if (!isMatchType.value) return false
  if (!form.value._season) return false
  const s = seasonPlayoffState.value
  return s && (s.playoffActive || s.playoffGroupsActive || s.hasPlayoffConfig)
})

const isPlayoffPhase = computed(() => form.value.phase === 'playoff')
const isGroupsPhase = computed(() => form.value.phase === 'groups')
const isConsolationPhase = computed(() => form.value.phase === 'consolation')

const phaseOptions = computed(() => {
  const s = seasonPlayoffState.value
  if (!s) return []
  const opts: { value: string; label: string; icon: string }[] = []
  if (s.hasGroups) opts.push({ value: 'groups', label: t('eventForm.playoffPhaseGroups'), icon: 'mdi-account-group' })
  opts.push({ value: 'playoff', label: t('eventForm.playoffPhasePlayoff'), icon: 'mdi-trophy-outline' })
  if (s.consolationGroupEnabled) opts.push({ value: 'consolation', label: t('eventForm.playoffPhaseConsolation'), icon: 'mdi-shield-half-full' })
  return opts
})

const selectedPhaseLabel = computed(() => {
  const p = form.value.phase
  if (!p) return t('eventForm.playoffSelectPhase')
  return phaseOptions.value.find(o => o.value === p)?.label || p
})

const roundTypeLabelMap: Record<string, string> = {
  preliminary: 'eventForm.playoffRoundPreliminary',
  quarterfinal: 'eventForm.playoffRoundQuarterfinal',
  semifinal: 'eventForm.playoffRoundSemifinal',
  final: 'eventForm.playoffRoundFinal',
  bronze: 'eventForm.playoffRoundBronze',
  round_of_16: 'eventForm.playoffRoundOf16',
}

const roundTypeOptions = computed(() => {
  const branches = seasonPlayoffState.value?.branches || []
  const seen = new Set<string>()
  const opts: { value: string; label: string }[] = []
  for (const b of branches) {
    for (const r of b.rounds || []) {
      if (!seen.has(r.name)) {
        seen.add(r.name)
        const key = roundTypeLabelMap[r.name]
        opts.push({ value: r.name, label: key ? t(key) : r.name })
      }
    }
  }
  return opts
})

const selectedRoundTypeLabel = computed(() => {
  const rt = form.value.playoffMeta?.roundType
  if (!rt) return t('eventForm.playoffSelectRound')
  if (rt === 'group_match') return t('eventForm.playoffRoundGroupMatch')
  const opt = roundTypeOptions.value.find(o => o.value === rt)
  if (opt) return opt.label
  const key = roundTypeLabelMap[rt]
  return key ? t(key) : rt
})

const selectedBranchLabel = computed(() => {
  const bid = form.value.playoffMeta?.branchId
  if (bid == null) return t('eventForm.playoffSelectBranch')
  const branches = seasonPlayoffState.value?.branches || []
  return branches.find(b => b.id === bid)?.name || t('eventForm.playoffBranch') + ` ${bid}`
})

const fetchSeasonPlayoffState = async (seasonId: string) => {
  try {
    const { data } = await axiosInstance.get(`/season/${seasonId}/playoff-state`)
    seasonPlayoffState.value = {
      playoffActive: !!data.playoffActive,
      playoffGroupsActive: !!data.playoffGroupsActive,
      hasPlayoffConfig: !!data.hasPlayoffConfig,
      hasGroups: !!data.hasGroups,
      consolationGroupEnabled: !!data.consolationGroupEnabled,
      branches: data.branches || [],
      groupCount: data.groupCount || 0,
      totalTeams: data.totalTeams || 0,
      teamsPerGroup: data.teamsPerGroup || 0,
      regularSeasonGroupCount: data.regularSeasonGroupCount || 1,
    }
  } catch {
    seasonPlayoffState.value = null
  }
}

const initPlayoffMeta = () => {
  if (!form.value.playoffMeta) {
    form.value.playoffMeta = {
      roundType: '',
      branchId: null,
      seriesIndex: 0,
      gameNumber: 1,
      label: '',
      groupIndex: null,
      round: null,
      isDraft: true,
      groupSlotHome: null,
      groupSlotAway: null,
      groupSlotHomeLabel: null,
      groupSlotAwayLabel: null,
    }
  }
}

const togglePlayoffEvent = (val: boolean) => {
  if (val) {
    initPlayoffMeta()
  } else {
    form.value.playoffMeta = null
    form.value.phase = null
  }
}

const selectPlayoffPhase = (phase: string) => {
  initPlayoffMeta()
  form.value.phase = phase
  // Reset round type when switching phase
  if (phase === 'groups' || phase === 'consolation') {
    form.value.playoffMeta!.roundType = 'group_match'
    form.value.playoffMeta!.branchId = null
    form.value.playoffMeta!.seriesIndex = 0
  } else {
    form.value.playoffMeta!.roundType = ''
    form.value.playoffMeta!.groupIndex = null
  }
  currentView.value = 'main'
}

const selectPlayoffRound = (roundType: string) => {
  initPlayoffMeta()
  form.value.playoffMeta!.roundType = roundType
  currentView.value = 'main'
}

const selectPlayoffBranch = (branchId: number) => {
  initPlayoffMeta()
  form.value.playoffMeta!.branchId = branchId
  currentView.value = 'main'
}

const groupLetters = 'ABCDEFGHIJKLMNOP'

const groupOptions = computed(() => {
  const count = seasonPlayoffState.value?.groupCount || 0
  return Array.from({ length: count }, (_, i) => ({
    value: i,
    label: `${t('eventForm.playoffGroupLabel')} ${groupLetters[i] || i + 1}`,
  }))
})

const selectedGroupLabel = computed(() => {
  const gi = form.value.playoffMeta?.groupIndex
  if (gi == null) return t('eventForm.playoffSelectGroup')
  return `${t('eventForm.playoffGroupLabel')} ${groupLetters[gi] || gi + 1}`
})

const selectGroupIndex = (index: number) => {
  initPlayoffMeta()
  form.value.playoffMeta!.groupIndex = index
  // Clear slot labels when switching group
  form.value.playoffMeta!.groupSlotHomeLabel = null
  form.value.playoffMeta!.groupSlotAwayLabel = null
  currentView.value = 'main'
}

// ── Slot label options (for placeholder team assignment) ──
const slotLabelOptions = computed(() => {
  const state = seasonPlayoffState.value
  if (!state) return []
  const gc = state.groupCount || 0
  const phase = form.value.phase
  const totalTeams = state.totalTeams || 0
  const rsGroupCount = state.regularSeasonGroupCount || 1

  // Groups phase (skupinová fáze)
  if (phase === 'groups') {
    if (rsGroupCount <= 1) {
      // Jedna skupina v ZČ → ZČ-1 až ZČ-{totalTeams}
      const count = totalTeams || 12
      return Array.from({ length: count }, (_, i) => ({
        value: `ZČ-${i + 1}`,
        label: `ZČ-${i + 1}`,
      }))
    }
    // Více skupin v ZČ → ZČ-A-1, ZČ-A-2, ZČ-B-1, ZČ-B-2...
    const tpg = Math.ceil(totalTeams / rsGroupCount)
    const opts: { value: string; label: string }[] = []
    for (let gi = 0; gi < rsGroupCount; gi++) {
      const letter = groupLetters[gi] || String(gi + 1)
      for (let pos = 1; pos <= tpg; pos++) {
        opts.push({ value: `ZČ-${letter}-${pos}`, label: `ZČ-${letter}-${pos}` })
      }
    }
    return opts
  }

  // Bracket (playoff) phase: A-1, A-2, B-1, B-2, ...
  if (phase === 'playoff' && gc > 0) {
    const tpg = state.teamsPerGroup || Math.ceil((totalTeams || gc * 2) / gc)
    const opts: { value: string; label: string }[] = []
    for (let gi = 0; gi < gc; gi++) {
      const letter = groupLetters[gi] || String(gi + 1)
      for (let pos = 1; pos <= tpg; pos++) {
        opts.push({ value: `${letter}-${pos}`, label: `${letter}-${pos}` })
      }
    }
    return opts
  }

  // Consolation phase: #1, #2, ...
  if (phase === 'consolation') {
    const count = totalTeams || (gc > 0 ? gc * 2 : 8)
    return Array.from({ length: count }, (_, i) => ({
      value: `#${i + 1}`,
      label: `#${i + 1}`,
    }))
  }

  // Fallback: ZČ-N labels
  const count = totalTeams || 12
  return Array.from({ length: count }, (_, i) => ({
    value: `ZČ-${i + 1}`,
    label: `ZČ-${i + 1}`,
  }))
})

const pendingSlotSide = ref<'home' | 'away'>('home')

const selectSlotLabel = (label: string) => {
  initPlayoffMeta()
  if (pendingSlotSide.value === 'home') {
    form.value.playoffMeta!.groupSlotHomeLabel = label
  } else {
    form.value.playoffMeta!.groupSlotAwayLabel = label
  }
  currentView.value = 'main'
}

const eventIconOptions = computed(() => [
  { value: 'default', icon: 'mdi-hockey-sticks', label: t('eventForm.iconGame') },
  { value: 'hockey-puck', icon: 'mdi-hockey-puck', label: t('eventForm.iconPuck') },
  { value: 'whistle', icon: 'mdi-whistle', label: t('eventForm.iconWhistle') },
  { value: 'run', icon: 'mdi-run', label: t('eventForm.iconRun') },
  { value: 'weight', icon: 'mdi-weight-lifter', label: t('eventForm.iconTraining') },
  { value: 'camp', icon: 'mdi-tent', label: t('eventForm.iconCamp') },
  { value: 'strategy', icon: 'mdi-strategy', label: t('eventForm.iconTactics') },
  { value: 'video', icon: 'mdi-video', label: t('eventForm.iconVideo') },
])

// ── Event types from API ──
type EventTypeItem = {
  _id: string
  name: string
  value: string
  color: string
  _league: string | null
  _season: string | null
  groupsCount: number
  availableTeams?: Array<{ _id: string; name: string; imageUrl?: string | null; colors?: string[] }>
  eventsPresets: {
    partsCount: number; partTime: number; overtimeDuration?: number; penaltySmall: number; penaltyBig: number
    penaltyPersonal: number; playersCount: string; queue: boolean; allInDefault: boolean
    public: boolean; allowExternals: boolean
  }
}

const selectedType = ref<EventTypeItem | null>(null)
const eventTypes = ref<EventTypeItem[]>([])
const eventTypesLoading = ref(false)
const eventTypesFetched = ref(false)

const isSelectedType = (et: EventTypeItem): boolean => {
  if (!selectedType.value) return false
  return selectedType.value.value === et.value
    && selectedType.value._league === et._league
    && selectedType.value._season === et._season
}

const isMatchType = computed(() => {
  const t = form.value.type
  if (!t) return false
  // Vše kromě training, blue-white a camp je match type
  return t !== 'training' && t !== 'blue-white' && t !== 'camp'
})

// Show icon picker for training and camp types
const showIconPicker = computed(() => {
  return form.value.type === 'training' || form.value.type === 'camp'
})

const selectedIconLabel = computed(() => {
  const opt = eventIconOptions.value.find(o => o.value === form.value.icon)
  return opt?.label || t('eventForm.jerseySelect')
})

const selectedIconMdi = computed(() => {
  const opt = eventIconOptions.value.find(o => o.value === form.value.icon)
  return opt?.icon || 'mdi-help-circle-outline'
})

const gameSettingsSummary = computed(() => {
  return `${form.value.partsCount}×${form.value.partTime} min, ${form.value.playersCount}`
})

// ── Match score (stav utkání) ──
const scoreEnabled = ref(false)
const scoreConfirmDialog = ref(false)
const scoreDirty = ref(false)

// ── Description editor ──
const descriptionOpen = ref(false)
const descEditorRef = ref<HTMLDivElement | null>(null)
const descBold = ref(false)
const descHeading = ref<'none' | 'h3' | 'h4'>('none')
const descActiveColor = ref('default')
const descDirty = ref(false)
const descScrollTop = ref(false)
const descScrollBot = ref(false)

const updateDescScroll = () => {
  const el = descEditorRef.value
  if (!el) return
  descScrollTop.value = el.scrollTop > 4
  descScrollBot.value = el.scrollTop + el.clientHeight < el.scrollHeight - 4
}

const toggleHeading = (tag: 'h3' | 'h4') => {
  if (descHeading.value === tag) {
    // Turn off heading → back to paragraph
    document.execCommand('formatBlock', false, 'p')
    descHeading.value = 'none'
  } else {
    // Switch to the requested heading
    document.execCommand('formatBlock', false, tag)
    descHeading.value = tag
  }
  // Sync form
  if (descEditorRef.value) form.value.description = descEditorRef.value.innerHTML
  descEditorRef.value?.focus()
}

// Track current block on selection change
const updateToolbarState = () => {
  const sel = window.getSelection()
  if (!sel || !sel.rangeCount || !descEditorRef.value) return
  let node: Node | null = sel.anchorNode
  let foundHeading: 'h3' | 'h4' | 'none' = 'none'
  let foundBold = false
  while (node && node !== descEditorRef.value) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = (node as HTMLElement).tagName.toLowerCase()
      if (tag === 'h3') foundHeading = 'h3'
      if (tag === 'h4') foundHeading = 'h4'
      if (tag === 'b' || tag === 'strong') foundBold = true
      const fw = (node as HTMLElement).style.fontWeight
      if (fw === 'bold' || Number(fw) >= 700) foundBold = true
    }
    node = node.parentNode
  }
  descHeading.value = foundHeading
  descBold.value = foundBold || document.queryCommandState('bold')
}

const descColorOptions = [
  { value: 'default', hex: 'rgb(var(--v-theme-sheetText))', label: 'Default' },
  { value: 'primary', hex: '#007AFF', label: 'Blue' },
  { value: 'success', hex: '#34C759', label: 'Green' },
  { value: 'warning', hex: '#FF9500', label: 'Orange' },
  { value: 'error', hex: '#FF3B30', label: 'Red' },
]

const toggleDescBold = () => {
  document.execCommand('bold', false)
  descBold.value = !descBold.value
  descEditorRef.value?.focus()
}

const getDefaultTextColor = (): string => {
  if (!descEditorRef.value) return '#000000'
  const style = getComputedStyle(descEditorRef.value)
  return style.color || '#000000'
}

const applyDescColor = (color: string) => {
  descActiveColor.value = color
  const hex = color === 'default'
    ? getDefaultTextColor()
    : descColorOptions.find(c => c.value === color)?.hex
  if (hex) {
    document.execCommand('foreColor', false, hex)
  }
  descEditorRef.value?.focus()
}

const onDescInput = () => {
  if (!descEditorRef.value) return
  form.value.description = descEditorRef.value.innerHTML
  descDirty.value = true
  updateToolbarState()
  nextTick(updateDescScroll)
}

const onDescKeydown = (e: KeyboardEvent) => {
  if (e.key === 'b' && (e.metaKey || e.ctrlKey)) {
    descBold.value = !descBold.value
  }
  nextTick(updateToolbarState)
}

const syncDescEditor = () => {
  if (form.value.description) {
    descriptionOpen.value = true
    nextTick(() => {
      if (descEditorRef.value) {
        descEditorRef.value.innerHTML = form.value.description
        nextTick(updateDescScroll)
      }
    })
  }
}

// ── Team change warning ──
const teamChangeWarningDialog = ref(false)
const pendingTeamChangeSide = ref<'home' | 'away'>('home')

const handleTeamClick = (side: 'home' | 'away') => {
  const currentTeamId = side === 'home' ? form.value.meta.homeTeam : form.value.meta.awayTeam
  if (isEditing.value && currentTeamId) {
    pendingTeamChangeSide.value = side
    teamChangeWarningDialog.value = true
  } else {
    currentView.value = side === 'home' ? 'homeTeamSelect' : 'awayTeamSelect'
  }
}

const confirmTeamChange = () => {
  teamChangeWarningDialog.value = false
  currentView.value = pendingTeamChangeSide.value === 'home' ? 'homeTeamSelect' : 'awayTeamSelect'
}
const matchScoreHome = ref<number | null>(null)
const matchScoreAway = ref<number | null>(null)
const matchResolution = ref<'regular' | 'overtime' | 'shootouts'>('regular')

const eventHasPlayByPlay = computed(() => {
  const pbp = props.eventToEdit?.playByPlay
  return Array.isArray(pbp) && pbp.length > 0
})

const eventHasRosters = computed(() => {
  const gs = props.eventToEdit?.gameStatus
  return gs?.home?.roster?.length > 0 || gs?.away?.roster?.length > 0
})

const scoreConfirmMessage = computed(() => {
  const parts: string[] = [t('eventForm.scoreConfirmBase')]
  if (!eventHasRosters.value) {
    parts.push(t('eventForm.scoreConfirmNoRosters'))
  } else {
    parts.push(t('eventForm.scoreConfirmWithRosters'))
  }
  if (eventHasPlayByPlay.value) {
    parts.push(t('eventForm.scoreConfirmPbpWillBeDeleted'))
  }
  parts.push(t('eventForm.scoreConfirmContinue'))
  return parts.join(' ')
})

const onScoreToggle = (val: boolean) => {
  if (val) {
    scoreConfirmDialog.value = true
  } else {
    scoreDirty.value = false
    scoreEnabled.value = false
    matchScoreHome.value = null
    matchScoreAway.value = null
    matchResolution.value = 'regular'
  }
}

const confirmScoreToggle = () => {
  scoreConfirmDialog.value = false
  scoreEnabled.value = true
  scoreDirty.value = true
}

const markScoreDirty = () => {
  scoreDirty.value = true
}

const selectMatchResolution = (value: 'regular' | 'overtime' | 'shootouts') => {
  matchResolution.value = value
  scoreDirty.value = true
}

const showResolutionPicker = computed(() => {
  const h = matchScoreHome.value
  const a = matchScoreAway.value
  if (typeof h !== 'number' || typeof a !== 'number' || isNaN(h) || isNaN(a)) return false
  return Math.abs(h - a) === 1
})

const hasMatchScore = computed(() => {
  const h = matchScoreHome.value
  const a = matchScoreAway.value
  return scoreEnabled.value
    && typeof h === 'number' && typeof a === 'number'
    && !isNaN(h) && !isNaN(a)
    && (h > 0 || a > 0)
    && h !== a
})

const shouldSimulateScore = computed(() => scoreDirty.value && hasMatchScore.value)

const resolutionOptions = computed(() => [
  { value: 'regular' as const, label: t('eventForm.resolutionRegular') },
  { value: 'overtime' as const, label: t('eventForm.resolutionOvertime') },
  { value: 'shootouts' as const, label: t('eventForm.resolutionShootouts') },
])

const selectIcon = (value: string) => {
  form.value.icon = value
  currentView.value = 'main'
}

const applyPresets = (p?: EventTypeItem['eventsPresets']) => {
  if (!p) return
  form.value.partsCount = p.partsCount ?? form.value.partsCount
  form.value.partTime = p.partTime ?? form.value.partTime
  form.value.overtimeDuration = p.overtimeDuration ?? form.value.overtimeDuration
  form.value.penaltySmall = p.penaltySmall ?? form.value.penaltySmall
  form.value.penaltyBig = p.penaltyBig ?? form.value.penaltyBig
  form.value.penaltyPersonal = p.penaltyPersonal ?? form.value.penaltyPersonal
  form.value.playersCount = p.playersCount ?? form.value.playersCount
  form.value.queue = p.queue ?? form.value.queue
  form.value.allInDefault = p.allInDefault ?? form.value.allInDefault
  form.value.public = p.public ?? form.value.public
  form.value.allowExternals = p.allowExternals ?? form.value.allowExternals
}

const selectEventType = (et: EventTypeItem) => {
  selectedType.value = et
  form.value.type = et.value
  form.value._league = et._league
  form.value._season = et._season

  // Fetch playoff state for this season
  if (et._season) {
    fetchSeasonPlayoffState(et._season)
  } else {
    seasonPlayoffState.value = null
    form.value.phase = null
    form.value.playoffMeta = null
  }

  if (et.value === 'training') form.value.icon = 'weight'
  else if (et.value === 'camp') form.value.icon = 'camp'
  else form.value.icon = 'default'

  applyPresets(et.eventsPresets)

  form.value.meta.players = 20
  form.value.meta.goalies = 2
  form.value.meta.homePlayers = 15
  form.value.meta.homeGoalies = 1
  form.value.meta.awayPlayers = 15
  form.value.meta.awayGoalies = 1

  if (et.value === 'blue-white') loadBlueWhiteTeams(et)

  // Duration
  const duration = (et.value === 'training' || et.value === 'friendly-match') ? 60 : 75
  if (form.value.timeFrom) form.value.timeTo = addMinutesToTime(form.value.timeFrom, duration)

  // Auto-fill price from last event of same type
  fetchLastPrice(et.value, form.value._team)

  // Auto-fill venue (+ game settings for non-league types)
  if (et._season) {
    fetchLastDefaults({ type: et.value, season: et._season })
  } else if (et.value === 'blue-white' && form.value._team) {
    fetchLastDefaults({ type: 'blue-white', team: form.value._team })
  }
  // friendly-match defaults are fetched when home team is selected
}

const fetchLastPrice = async (type: string, team?: string | null) => {
  try {
    const params: any = { type }
    if (type === 'blue-white' && team) params.team = team
    const { data } = await axiosInstance.get('/events/last-price', { params })
    form.value.ammountCzk = data.ammountCzk ?? 350
    form.value.ammountGoalieCzk = data.ammountGoalieCzk ?? 0
    form.value.allInDefault = data.allInDefault ?? false
  } catch {
    form.value.ammountCzk = 350
    form.value.ammountGoalieCzk = 0
  }
}

const fetchLastDefaults = async (opts: { type: string; team?: string | null; season?: string | null; homeTeam?: string | null }) => {
  try {
    const params: any = { type: opts.type }
    if (opts.team) params.team = opts.team
    if (opts.season) params.season = opts.season
    if (opts.homeTeam) params.homeTeam = opts.homeTeam
    const { data } = await axiosInstance.get('/events/last-defaults', { params })

    // Pre-fill venue
    if (data.place) form.value.place = data.place

    // Pre-fill game settings only for non-league types (league gets them from season presets)
    if (opts.type === 'blue-white' || opts.type === 'friendly-match') {
      if (data.partsCount != null) form.value.partsCount = data.partsCount
      if (data.partTime != null) form.value.partTime = data.partTime
      if (data.overtimeDuration != null) form.value.overtimeDuration = data.overtimeDuration
      if (data.penaltySmall != null) form.value.penaltySmall = data.penaltySmall
      if (data.penaltyBig != null) form.value.penaltyBig = data.penaltyBig
      if (data.penaltyPersonal != null) form.value.penaltyPersonal = data.penaltyPersonal
      if (data.playersCount != null) form.value.playersCount = data.playersCount
    }
  } catch {
    // silent – keep defaults
  }
}

const fetchEventTypes = async () => {
  if (!userStore.isAuthenticated) { eventTypes.value = []; return }
  eventTypesLoading.value = true
  try {
    const { data } = await axiosInstance.get('/users/availableevents')
    eventTypes.value = data.eventTypes || []
    if (eventTypes.value.length && !props.eventToEdit) {
      let preset: EventTypeItem | undefined | null = null
      if (props.presetEventType) {
        // Prefer exact league match when provided (important for academy training types).
        if (props.presetLeagueId) {
          preset = eventTypes.value.find(
            et => et.value === props.presetEventType && et._league === props.presetLeagueId
          )
        }

        // Then prefer global (non-league) type, then any matching value.
        preset = preset
          || eventTypes.value.find(et => et.value === props.presetEventType && !et._league)
          || eventTypes.value.find(et => et.value === props.presetEventType)
      } else if (props.presetLeagueId && props.presetSeasonId) {
        preset = eventTypes.value.find(et => et._league === props.presetLeagueId && et._season === props.presetSeasonId)
      } else if (props.presetTeamId) {
        preset = eventTypes.value.find(et => et.value === 'blue-white' && !et._league)
      }
      selectEventType(preset || eventTypes.value[0])
      if (props.presetTeamId) {
        form.value._team = props.presetTeamId
        form.value.meta.homeTeam = props.presetTeamId
      }
      if (props.presetHomeTeamId) {
        form.value.meta.homeTeam = props.presetHomeTeamId
      }
      if (props.presetAwayTeamId) {
        form.value.meta.awayTeam = props.presetAwayTeamId
      }
    }
  } catch {
    // silent
  } finally {
    eventTypesLoading.value = false
    eventTypesFetched.value = true
  }
}

// ── Teams ──
const loadBlueWhiteTeams = (et: EventTypeItem) => {
  const teams = (et.availableTeams ?? []) as any[]
  if (!form.value._team && teams.length) {
    form.value._team = teams[0]._id
  }
}

const availableTeamsForForm = computed(() => {
  const list = selectedType.value?.availableTeams ?? []
  return list.map(t => ({
    _id: String((t as any)._id),
    name: (t as any).name,
    imageUrl: (t as any).imageUrl ?? null,
    colors: (t as any).colors ?? [],
  }))
})
const availableHomeTeams = computed(() =>
  availableTeamsForForm.value.filter(t => t._id !== form.value.meta.awayTeam)
)
const availableAwayTeams = computed(() =>
  availableTeamsForForm.value.filter(t => t._id !== form.value.meta.homeTeam)
)

const recomputeColors = () => {
  const home = availableTeamsForForm.value.find(t => t._id === form.value.meta.homeTeam)
  const away = availableTeamsForForm.value.find(t => t._id === form.value.meta.awayTeam)
  if (home && away) {
    const colors = defaultDressColor(home.colors ?? [], away.colors ?? [])
    form.value.meta.homeColor = colors.home ?? null
    form.value.meta.awayColor = colors.away ?? null
  } else {
    if (!home) form.value.meta.homeColor = null
    if (!away) form.value.meta.awayColor = null
  }
}
const onTeamChange = () => { recomputeColors() }

const homeTeamColors = computed(() => {
  const ht = availableTeamsForForm.value.find(t => t._id === form.value.meta?.homeTeam)
  return (ht?.colors ?? []) as string[]
})
const awayTeamColors = computed(() => {
  const at = availableTeamsForForm.value.find(t => t._id === form.value.meta?.awayTeam)
  return (at?.colors ?? []) as string[]
})

const selectedHomeTeam = computed(() =>
  availableTeamsForForm.value.find(t => t._id === form.value.meta.homeTeam) ?? null
)
const selectedAwayTeam = computed(() =>
  availableTeamsForForm.value.find(t => t._id === form.value.meta.awayTeam) ?? null
)
const selectedBlueWhiteTeam = computed(() =>
  availableTeamsForForm.value.find(t => t._id === form.value._team) ?? null
)

const selectHomeTeam = (team: { _id: string }) => {
  form.value.meta.homeTeam = team._id
  onTeamChange()
  currentView.value = 'main'
  // For friendly matches, fetch venue + game settings from last friendly of this home team
  if (form.value.type === 'friendly-match') {
    fetchLastDefaults({ type: 'friendly-match', homeTeam: team._id })
  }
}
const selectAwayTeam = (team: { _id: string }) => {
  form.value.meta.awayTeam = team._id
  onTeamChange()
  currentView.value = 'main'
}
const removeTeam = (side: 'home' | 'away') => {
  if (side === 'home') {
    form.value.meta.homeTeam = null
    form.value.meta.homeColor = null
  } else {
    form.value.meta.awayTeam = null
    form.value.meta.awayColor = null
  }
  currentView.value = 'main'
}
const selectBlueWhiteTeam = (team: { _id: string }) => {
  form.value._team = team._id
  currentView.value = 'main'
  // Re-fetch price and defaults for this team
  fetchLastPrice(form.value.type, team._id)
  fetchLastDefaults({ type: 'blue-white', team: team._id })
}

const togglePlayersCount = () => {
  form.value.playersCount = form.value.playersCount === '5+1' ? '3+1' : '5+1'
}

// ── Jersey picker subview ──
const jerseyPickerSide = ref<'home' | 'away'>('home')

const openJerseyPicker = (side: 'home' | 'away') => {
  jerseyPickerSide.value = side
  currentView.value = side === 'home' ? 'homeJersey' : 'awayJersey'
}

const jerseyPickerColors = computed(() => {
  return jerseyPickerSide.value === 'home' ? homeTeamColors.value : awayTeamColors.value
})

const jerseyPickerValue = computed(() => {
  return jerseyPickerSide.value === 'home' ? form.value.meta.homeColor : form.value.meta.awayColor
})

const jerseyPickerTeamImage = computed(() => {
  const teamId = jerseyPickerSide.value === 'home' ? form.value.meta.homeTeam : form.value.meta.awayTeam
  if (!teamId) return null
  const team = availableTeamsForForm.value.find(t => t._id === teamId)
  return team?.imageUrl ?? null
})

const selectJersey = (color: string) => {
  if (jerseyPickerSide.value === 'home') {
    form.value.meta.homeColor = color
  } else {
    form.value.meta.awayColor = color
  }
  currentView.value = 'main'
}

// ── Time helpers ──
const addMinutesToTime = (time: string, minutesToAdd: number): string => {
  if (!time || !time.includes(':')) return ''
  const normalized = normalizeTime(time)
  if (!normalized) return ''
  const [hours, minutes] = normalized.split(':').map(Number)
  const date = new Date()
  date.setHours(hours)
  date.setMinutes(minutes + minutesToAdd)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

// Safari-safe time normalization
const normalizeTime = (val: string): string => {
  if (!val) return ''
  const trimmed = val.trim().replace(/\s+/g, ' ')
  if (/^\d{2}:\d{2}$/.test(trimmed)) return trimmed
  if (/^\d:\d{2}$/.test(trimmed)) return '0' + trimmed
  const normalized = trimmed.replace(/\s*:\s*/g, ':')
  const ampmMatch = normalized.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|am|pm|a\.m\.|p\.m\.)$/i)
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1], 10)
    const minutes = ampmMatch[2]
    const period = ampmMatch[3].toUpperCase().replace(/\./g, '')
    if (period === 'PM' && hours !== 12) hours += 12
    else if (period === 'AM' && hours === 12) hours = 0
    return `${String(hours).padStart(2, '0')}:${minutes}`
  }
  if (trimmed.includes(':')) {
    const parts = trimmed.split(':')
    if (parts.length >= 2) {
      const h = parseInt(parts[0], 10)
      const m = parseInt(parts[1], 10)
      if (!isNaN(h) && !isNaN(m)) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    }
  }
  return ''
}

const formatDate = (date: Date): string => {
  const y = date.getFullYear()
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ── Validation ──
const canSubmit = computed(() => {
  return form.value.type && form.value.dateFrom && form.value.timeFrom && (hasChanges.value || shouldSimulateScore.value || descDirty.value)
})

// ── Save ──
const isSaving = ref(false)

const handleSave = async () => {
  if (isSaving.value) return
  isSaving.value = true

  try {
    const normalizedTimeFrom = normalizeTime(form.value.timeFrom)
    const normalizedTimeTo = normalizeTime(form.value.timeTo)

    const eventData = {
      ...form.value,
      timeFrom: normalizedTimeFrom,
      timeTo: normalizedTimeTo,
      timekeepers: [...form.value.timekeepers],
      weeklyCopy: Math.min(Math.max(0, form.value.weeklyCopy || 0), 10),
    }

    console.log('[EventFormModal] Odesílám eventData:', JSON.stringify(eventData, null, 2))

    let response
    if (props.eventToEdit?._id) {
      response = await axiosInstance.put(`/events/${props.eventToEdit._id}`, eventData)
    } else {
      response = await axiosInstance.post('/events', eventData)
    }
    console.log('[EventFormModal] Server response:', JSON.stringify(response.data, null, 2))

    // If match score was explicitly entered/changed, trigger score-based simulation.
    // When editing a completed match, score is prefilled from gameStatus only for display;
    // saving regular event details must not regenerate play-by-play.
    const savedEventId = response.data?.event?._id || response.data?._id || props.eventToEdit?._id
    console.log('[EventFormModal] shouldSimulateScore:', shouldSimulateScore.value, 'savedEventId:', savedEventId, 'home:', matchScoreHome.value, 'away:', matchScoreAway.value)
    if (shouldSimulateScore.value && savedEventId) {
      try {
        const resolution = showResolutionPicker.value ? matchResolution.value : 'regular'
        const scorePayload = {
          score: { home: Number(matchScoreHome.value), away: Number(matchScoreAway.value) },
          resolution,
        }
        console.log('[EventFormModal] Odesílám simulaci:', JSON.stringify(scorePayload))
        await axiosInstance.post(`/leagues/event/${savedEventId}/simulate`, scorePayload)
        console.log('[EventFormModal] Simulace OK')
      } catch (err: any) {
        console.error('[EventFormModal] Chyba při simulaci skóre:', err?.response?.data || err?.message || err)
      }
    }

    emit('saved', response.data)
    isOpen.value = false
  } catch (error) {
    console.error('[EventFormModal] Chyba při ukládání události:', error)
  } finally {
    isSaving.value = false
  }
}

const handleClose = () => {
  currentView.value = 'main'
}

// ── Reset on open ──
const resetForm = () => {
  descriptionOpen.value = false
  descBold.value = false
  descHeading.value = 'none'
  descActiveColor.value = 'default'
  descDirty.value = false
  const today = props.initialDate ? formatDate(props.initialDate) : formatDate(new Date())
  form.value = {
    title: '',
    description: '',
    type: '',
    dateFrom: today,
    timeFrom: '',
    dateTo: today,
    timeTo: '',
    place: 'Haly Lužánky',
    meta: { players: 20, goalies: 2 },
    attendants: {},
    pin: false,
    weeklyCopy: 0,
    _league: null,
    _season: null,
    content: '',
    allowExternals: false,
    public: false,
    group: null,
    _team: null,
    queue: false,
    allInDefault: false,
    partsCount: 3,
    partTime: 15,
    overtimeDuration: 10,
    penaltySmall: 1.5,
    penaltyBig: 3,
    penaltyPersonal: 7,
    playersCount: '5+1',
    splitPointsByParts: false,
    pointsWin: 3,
    pointsOvertimeWin: 2,
    pointsOvertimeLoss: 1,
    timekeepers: [],
    icon: 'default',
    ammountCzk: 350,
    ammountGoalieCzk: 0,
    phase: null,
    playoffMeta: null,
  }
  currentView.value = 'main'
  selectedType.value = null
  seasonPlayoffState.value = null
  scoreEnabled.value = false
  scoreDirty.value = false
  matchScoreHome.value = null
  matchScoreAway.value = null
  matchResolution.value = 'regular'
}

// ── Fill from edit ──
const fillFromEdit = (ev: any) => {
  const toISODate = (val: any): string => {
    if (!val) return ''
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) return val.slice(0, 10)
    const d = new Date(val)
    if (isNaN(d.getTime())) return ''
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  const toHHMM = (val: any): string => {
    if (!val) return ''
    if (typeof val === 'string' && /^\d{2}:\d{2}$/.test(val)) return val
    const d = new Date(val)
    if (isNaN(d.getTime())) return ''
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }

  form.value = {
    ...form.value,
    title: ev.title ?? '',
    description: ev.description ?? '',
    type: ev.type ?? '',
    dateFrom: toISODate(ev.dateFromRaw ?? ev.dateFrom),
    timeFrom: toHHMM(ev.dateFromRaw) || ev.timeFrom || '',
    dateTo: toISODate(ev.dateToRaw ?? ev.dateTo) || toISODate(ev.dateFromRaw ?? ev.dateFrom),
    timeTo: toHHMM(ev.dateToRaw) || ev.timeTo || '',
    place: ev.place ?? 'Haly Lužánky',
    meta: ev.meta ? { ...ev.meta } : {},
    attendants: ev.attendants ?? {},
    pin: !!ev.pin,
    weeklyCopy: 0,
    content: ev.content ?? '',
    allowExternals: ev.allowExternals ?? false,
    public: ev.public ?? false,
    group: ev.group ?? null,
    _league: ev._league?._id ?? ev._league ?? null,
    _season: ev._season?._id ?? ev._season ?? null,
    _team: ev._team?._id ?? ev._team ?? null,
    queue: ev.queue ?? false,
    allInDefault: !!ev.allInDefault,
    partsCount: Number.isFinite(ev.partsCount) ? ev.partsCount : 3,
    partTime: Number.isFinite(ev.partTime) ? ev.partTime : 15,
    overtimeDuration: Number.isFinite(ev.overtimeDuration) ? ev.overtimeDuration : 10,
    penaltySmall: Number.isFinite(ev.penaltySmall) ? ev.penaltySmall : 1.5,
    penaltyBig: Number.isFinite(ev.penaltyBig) ? ev.penaltyBig : 3,
    penaltyPersonal: Number.isFinite(ev.penaltyPersonal) ? ev.penaltyPersonal : 7,
    playersCount: ev.playersCount || '5+1',
    splitPointsByParts: !!ev.splitPointsByParts,
    pointsWin: Number.isFinite(ev.pointsWin) ? ev.pointsWin : 3,
    pointsOvertimeWin: Number.isFinite(ev.pointsOvertimeWin) ? ev.pointsOvertimeWin : 2,
    pointsOvertimeLoss: Number.isFinite(ev.pointsOvertimeLoss) ? ev.pointsOvertimeLoss : 1,
    timekeepers: Array.isArray(ev.timekeepers)
      ? ev.timekeepers.map((x: any) => (typeof x === 'string' ? x : x._id))
      : [],
    icon: ev.icon || 'default',
    ammountCzk: Number.isFinite(ev.ammountCzk) ? ev.ammountCzk : 350,
    ammountGoalieCzk: Number.isFinite(ev.ammountGoalieCzk) ? ev.ammountGoalieCzk : 0,
    phase: ev.phase || null,
    playoffMeta: ev.playoffMeta || null,
  }

  if (form.value.meta.homeTeam) {
    form.value.meta.homeTeam = form.value.meta.homeTeam._id ?? form.value.meta.homeTeam
  }
  if (form.value.meta.awayTeam) {
    form.value.meta.awayTeam = form.value.meta.awayTeam._id ?? form.value.meta.awayTeam
  }

  // Fetch season playoff state for editing
  const seasonId = ev._season?._id ?? ev._season
  if (seasonId) fetchSeasonPlayoffState(seasonId)

  // Pre-fill match score from gameStatus if event is already completed
  const gs = ev.gameStatus
  if (gs?.matchCompleted && gs?.home?.goals != null && gs?.away?.goals != null) {
    scoreEnabled.value = true
    matchScoreHome.value = gs.home.goals
    matchScoreAway.value = gs.away.goals
    matchResolution.value = gs.shootouts ? 'shootouts' : gs.overtime ? 'overtime' : 'regular'
    scoreDirty.value = false
  } else {
    scoreEnabled.value = false
    scoreDirty.value = false
    matchScoreHome.value = null
    matchScoreAway.value = null
    matchResolution.value = 'regular'
  }

  const match =
    eventTypes.value.find(et =>
      et.value === ev.type &&
      et._league === (ev._league?._id ?? ev._league ?? null) &&
      et._season === (ev._season?._id ?? ev._season ?? null)
    ) ||
    eventTypes.value.find(et => et.value === ev.type) ||
    null
  selectedType.value = match

  nextTick(() => syncDescEditor())
}

// ── Watchers ──
watch(isOpen, async (open) => {
  if (open) {
    resetForm()
    // Naplň form PŘED await, aby UI mělo správná data hned
    if (props.eventToEdit) {
      fillFromEdit(props.eventToEdit)
    }
    await fetchEventTypes()
    // If editing and no date set (autogenerated playoff), fetch last event date or use today
    if (props.eventToEdit && !form.value.dateFrom) {
      const season = props.eventToEdit._season?._id ?? props.eventToEdit._season
      if (season) {
        try {
          const { data } = await axiosInstance.get('/events/last-defaults', {
            params: { type: props.eventToEdit.type, season }
          })
          if (data.dateFromRaw) {
            const d = new Date(data.dateFromRaw)
            if (!isNaN(d.getTime())) {
              form.value.dateFrom = formatDate(d)
              form.value.dateTo = formatDate(d)
            }
          }
          if (data.place && !form.value.place) {
            form.value.place = data.place
          }
        } catch { /* silent */ }
      }
      if (!form.value.dateFrom) {
        const today = formatDate(new Date())
        form.value.dateFrom = today
        form.value.dateTo = today
      }
    }
    // Po načtení typů znovu nastav selectedType
    if (props.eventToEdit) {
      const ev = props.eventToEdit
      const match =
        eventTypes.value.find(et =>
          et.value === ev.type &&
          et._league === (ev._league?._id ?? ev._league ?? null) &&
          et._season === (ev._season?._id ?? ev._season ?? null)
        ) ||
        eventTypes.value.find(et => et.value === ev.type) ||
        null
      selectedType.value = match
    }
    if (!props.eventToEdit) {
      recomputeColors()
    }
    // Snapshot after full init so hasChanges starts as false
    if (props.eventToEdit) {
      await nextTick()
      takeSnapshot()
    }
  }
})

watch(() => form.value.allInDefault, (val) => {
  if (val) form.value.queue = false
})

watch(() => form.value.timeFrom, (newVal) => {
  if (newVal && newVal.includes(':')) {
    const duration = (form.value.type === 'training' || form.value.type === 'friendly-match') ? 60 : 75
    form.value.timeTo = addMinutesToTime(newVal, duration)
  }
})

watch(() => form.value.dateFrom, (newVal) => {
  if (!props.eventToEdit) {
    form.value.dateTo = newVal
  }
  if (newVal && form.value.dateTo && form.value.dateTo < newVal) {
    form.value.dateTo = newVal
  }
})

watch(() => form.value.dateTo, (newVal) => {
  if (form.value.dateFrom && newVal && newVal < form.value.dateFrom) {
    form.value.dateTo = form.value.dateFrom
  }
})

// Ensure timeTo is not before timeFrom (when same date)
watch(() => form.value.timeTo, (newVal) => {
  if (form.value.dateFrom === form.value.dateTo && form.value.timeFrom && newVal && newVal < form.value.timeFrom) {
    form.value.timeTo = form.value.timeFrom
  }
})
</script>

<style lang="scss" scoped>
.views-container {
  position: relative;
  min-height: 400px;
}

.main-view {
  transition: opacity 0.2s ease, transform 0.2s ease;

  &--hidden {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }
}

.subview {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  height: 92vh;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-sheetBackground));
  border-radius: 32px 32px 0 0;
  z-index: 10000;
  overflow: hidden;
  transform: translateX(0);
}

.subview-header {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  
  > * {
    pointer-events: auto;
  }
}

.back-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgb(var(--v-theme-sheetCloseBtn));
  cursor: pointer;
  transition: all 0.2s ease;
  
  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }
  
  &:hover {
    filter: brightness(0.92);
  }
  
  &:active {
    transform: scale(0.95);
  }
}

.subview-title {
  flex: 1;
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  text-align: center;
}

.spacer {
  width: 45px;
}

.subview-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  padding-top: 70px; // space for fixed header
}

.ios-loading {
  display: flex;
  justify-content: center;
  padding: 16px;
}

// ── Inline date/time/number rows ──
.inline-datetime-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  min-height: 48px;
  background: rgb(var(--v-theme-sheetSection));
  border-bottom: 0.5px solid rgba(var(--v-theme-sheetText), 0.12);

  &.first {
    border-radius: 14px 14px 0 0;
  }

  &.last {
    border-radius: 0 0 14px 14px;
    border-bottom: none;
  }

  &.first.last {
    border-radius: 14px;
    border-bottom: none;
  }
}

.inline-label {
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
}

.inline-input {
  background: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
  text-align: right;
  max-width: 160px;
  font-family: 'Roboto Condensed', sans-serif;

  &::-webkit-calendar-picker-indicator {
    filter: invert(0.5);
  }

  &[type="number"] {
    max-width: 80px;
    appearance: textfield;
    -moz-appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      opacity: 1;
    }
  }
}

.price-input-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}

.price-suffix {
  font-size: 1rem;
  color: rgba(var(--v-theme-sheetText), 0.5);
  white-space: nowrap;
}

.inline-button {
  background: rgba(var(--v-theme-sheetText), 0.08);
  border: none;
  border-radius: 8px;
  padding: 6px 16px;
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  cursor: pointer;
  transition: background 0.15s;

  &:active {
    background: rgba(var(--v-theme-sheetText), 0.15);
  }
}

// ── Jersey picker grid (matches TeamFormModal) ──
.jerseys-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 16px;
}

.jersey-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 20px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;

  &--selected {
    border-color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.15);
  }
}

// ── Icon picker grid ──
.icons-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 16px;
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 8px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 16px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 90px;

  &--selected {
    border-color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.15);
  }
}

.icon-label {
  font-size: 0.7rem;
  font-weight: 500;
  color: rgba(var(--v-theme-sheetText), 0.7);
  text-align: center;
  margin-top: 8px;
}

// ── Section title (standalone - matches IosSection .section-title) ──
.section-title {
  font-size: 1rem;
  font-weight: 600;
  text-transform: none;
  color: rgb(var(--v-theme-sheetTextSecondary));
  padding: 0 16px;
  margin-bottom: 8px;
}

// ── Custom section wrapper (title + IosSection without double margin) ──
.custom-section {
  margin-bottom: 24px;
  
  :deep(.ios-section) {
    margin-bottom: 0;
  }
}

// ── Description toggle chevron ──
.desc-toggle-row__chevron {
  margin-left: auto;
  color: rgba(var(--v-theme-sheetText), 0.35) !important;
  transition: transform 0.25s ease;

  &.open {
    transform: rotate(180deg);
  }
}

// ── Description editor ──
.description-editor-wrap {
  overflow: hidden;
}

// ── Section collapse transition ──
.section-collapse-enter-active, .section-collapse-leave-active {
  transition: all 0.35s ease;
  overflow: hidden;
}
.section-collapse-enter-from, .section-collapse-leave-to {
  opacity: 0;
  max-height: 0 !important;
}
.section-collapse-enter-to, .section-collapse-leave-from {
  opacity: 1;
  max-height: 1000px;
}

.description-toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-bottom: 0.5px solid rgba(var(--v-theme-sheetText), 0.1);
}

.desc-tb-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(var(--v-theme-sheetText), 0.6);
  cursor: pointer;
  transition: all 0.15s;

  &.active {
    background: rgba(var(--v-theme-primary), 0.15);
    color: rgb(var(--v-theme-primary));
  }
}

.desc-tb-txt {
  font-weight: 800;
  font-size: 14px;
  line-height: 1;
  font-family: 'Barlow Condensed', 'Roboto Condensed', sans-serif;

  &--sm {
    font-size: 12px;
  }
}

.desc-tb-sep {
  width: 1px;
  height: 18px;
  background: rgba(var(--v-theme-sheetText), 0.1);
  margin: 0 4px;
}

.desc-tb-color {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2.5px solid rgba(var(--v-theme-sheetText), 0.12);
  background: var(--dot);
  cursor: pointer;
  transition: all 0.15s;
  position: relative;
  box-shadow: inset 0 0 0 2px rgba(255,255,255,0.25);

  &.active {
    border-color: var(--dot);
    box-shadow: 0 0 0 3px rgba(var(--v-theme-sheetText), 0.06), inset 0 0 0 2px rgba(255,255,255,0.4);
    transform: scale(1.15);
  }
}

.desc-editor-scroll-wrap {
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 20px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 1;
  }

  &::before {
    top: 0;
    background: linear-gradient(to bottom, rgb(var(--v-theme-sheetSection)), transparent);
  }

  &::after {
    bottom: 0;
    background: linear-gradient(to top, rgb(var(--v-theme-sheetSection)), transparent);
  }

  &.shadow-top::before { opacity: 1; }
  &.shadow-bot::after { opacity: 1; }
}

.description-editor {
  min-height: 80px;
  max-height: 200px;
  overflow-y: auto;
  padding: 12px 16px;
  font-size: 0.95rem;
  line-height: 1.55;
  color: rgb(var(--v-theme-sheetText));
  outline: none;
  -webkit-overflow-scrolling: touch;

  &:empty::before {
    content: attr(data-placeholder);
    color: rgba(var(--v-theme-sheetText), 0.3);
    pointer-events: none;
  }

  b, strong {
    font-weight: 700;
  }

  h3 {
    font-family: 'Barlow Condensed', 'Roboto Condensed', sans-serif;
    font-size: 1.2rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin: 4px 0 2px;
  }

  h4 {
    font-family: 'Barlow Condensed', 'Roboto Condensed', sans-serif;
    font-size: 1.05rem;
    font-weight: 700;
    margin: 2px 0 1px;
  }
}

// ── Optional hint ──
.optional-hint {
  font-size: 11px;
  font-weight: 400;
  text-transform: none;
  color: rgba(var(--v-theme-sheetText), 0.4);
}

// ── Team + Jersey row (side by side, isolated) ──
.team-jersey-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 24px;
  align-items: stretch;

  // Make both sections same height, remove their own margin
  :deep(.ios-section) {
    height: 100%;
    min-width: 0; // Allow grid child to shrink
    margin-bottom: 0;
    
    .ios-row {
      height: 100%;
    }

    .row-label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.jersey-dress-wrapper {
  position: relative;
  width: 100px;
  height: 80px;
  margin-bottom: 8px;
}

.jersey-dress-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.jersey-team-logo {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 28px;
  width: auto;
  object-fit: contain;
}

.jersey-label {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-sheetText), 0.7);
  text-align: center;
}

// ── Jersey preview in row ──
.jersey-preview {
  width: 40px;
  height: 32px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

// Text helpers
.text-disabled {
  color: rgba(var(--v-theme-sheetText), 0.4);
  font-size: 0.9rem;
}

// Slide transition
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from {
  transform: translateX(100%);
}

.slide-leave-to {
  transform: translateX(100%);
}

// ── Resolution picker ──
.resolution-picker {
  display: flex;
  gap: 6px;
}

.resolution-btn {
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.15);
  background: transparent;
  color: rgba(var(--v-theme-sheetText), 0.7);
  cursor: pointer;
  transition: all 0.15s ease;

  &.active {
    background: rgb(var(--v-theme-primary));
    color: #fff;
    border-color: rgb(var(--v-theme-primary));
  }
}

// ── Score team label ──
.score-team-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.score-team-avatar {
  flex-shrink: 0;
}

.score-team-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Score confirm dialog (CenteredModal) ── */
.score-confirm-dialog {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 20px 16px;
  text-align: center;
}

.score-confirm-icon {
  margin-bottom: 12px;
}

.score-confirm-title {
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 8px;
  color: rgb(var(--v-theme-on-surface));
}

.score-confirm-text {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  line-height: 1.5;
  margin-bottom: 20px;
}

.score-confirm-actions {
  display: flex;
  gap: 10px;
  width: 100%;
}

.score-confirm-btn {
  flex: 1;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}

.score-confirm-btn--cancel {
  background: rgba(var(--v-theme-on-surface), 0.08);
  color: rgb(var(--v-theme-on-surface));
}

.score-confirm-btn--confirm {
  background: #007AFF;
  color: #fff;
}
</style>
